import React, { useState, useEffect } from "react";

import PropTypes from "prop-types";
import { toast } from "react-toastify";
import Cookies from "universal-cookie";
import { useTranslation } from "react-i18next";

import { useTheme } from "@mui/material/styles";
import makeStyles from '@mui/styles/makeStyles';
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";

import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import PublishIcon from "@mui/icons-material/Publish";
import useMediaQuery from "@mui/material/useMediaQuery";

import {
  PdfViewerComponent,
  Toolbar,
  Magnification,
  Navigation,
  LinkAnnotation,
  BookmarkView,
  ThumbnailView,
  Print,
  TextSelection,
  TextSearch,
  Annotation,
  FormFields,
  FormDesigner,
  Inject as PDFInject,
} from "@syncfusion/ej2-react-pdfviewer";

import { jsPDF } from "jspdf";

const useStyles = makeStyles((theme) => ({
  labelToBtn: {
    color: "#fff",
    backgroundColor: "#1c355e",
    cursor: "pointer",
    outline: 0,
    border: 0,
    margin: 0,
    justifyContent: "center",
    verticalAlign: "middle",
    textAlign: "center",
    font: "400 13.3333px Arial",
    fontWeight: 600,
    lineHeight: 2,
    width: "200px",
    //letterSpacing: "0.02857em",
    textTransform: "uppercase",
    padding: "10px 16px",
    borderRadius: 4,
    "&:hover": {
      backgroundColor: "#132541",
      boxShadow:
        "0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%), 0px 1px 10px 0px rgb(0 0 0 / 12%)",
    },
  },
  tabButton: {
    [theme.breakpoints.up("sm")]: {
      paddingBottom: "20px",
      paddingLeft: "20px",
    },
    [theme.breakpoints.down('sm')]: {
      paddingBottom: "10px",
      textAlign: "center",
    },
  },
  tooltipPic: {
    width: 240,
    height: 240,
  },
  root: {
    flexGrow: 1,
    [theme.breakpoints.up("sm")]: {
      //width: "80vw",
      padding: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      //maxWidth: 400,
      paddingTop: theme.spacing(1),
      paddingRight: theme.spacing(1),
      paddingBottom: theme.spacing(2),
      paddingLeft: theme.spacing(1),
    },
  },
  pdfContainer: {
    //style={{ width: "640px", height: "640px" }}
    [theme.breakpoints.up("sm")]: { width: "80vw", maxWidth: 800 },
    [theme.breakpoints.down('sm')]: { width: "95vw" },
  },
}));

const initialFieldValues = {
  userId: 0,
  clientID: 0,
  uploadTypeID: 0,
  uploadFile: null,
  uploadFileType: null,
};

const UploadNow = ({
  selectedUploadTypeID,
  selectedUploadName,
  handleUploadDocument,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [imageData, setImageData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState();
  const [values, setValues] = useState(initialFieldValues);
  const [pdfSource, setPdfSource] = useState();
  const [viewPDFReader, setViewPDFReader] = useState();
  const [fileAsBlob, setFileAsBlob] = useState(null);
  const cookies = new Cookies();
  const user = cookies.get("lifesaferUser");
  const maxFileSize = 10 * 1024 * 1024;
  const theme = useTheme();
  const isXtraSmall = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (
      values != initialFieldValues &&
      values.userId != 0 &&
      values.clientID != 0 &&
      values.uploadTypeID != 0 &&
      values.uploadFile != null &&
      values.uploadFileType != null
    ) {
      sendFile();
    }
  }, [values]);

  const svcUrl = process.env.API_URL + "/PdfViewer";
  //THE FOLLOWING URL WAS ONLY FOR INITIAL SETUP/TESTING
  //const svcUrl =
  //  "https://ej2services.syncfusion.com/production/web-services/api/pdfviewer";
  // const pdfServiceLocal = pdfService.local;
  //const svcUrl = "https://devclientportalapi.lmgholdings.com/pdfViewer";

  const handleFileChange = (event) => {
    setPdfSource("");
    setViewPDFReader(false);
    setSubmitting(true);
    if (event.target.files && event.target.files[0]) {
      let selectedFile = event.target.files[0];
      const reader = new FileReader();
      if (selectedFile.type.slice(0, 6) === "image/") {
        reader.onload = (x) => {
          setFile({
            imageFile: selectedFile,
            imageSrc: x.target.result,
          });
        };
        reader.readAsDataURL(selectedFile);
      } else if (selectedFile.type === "application/pdf") {
        reader.onload = (x) => {
          setFileName(selectedUploadName + ".pdf");
          setFile({
            imageFile: selectedFile,
            imageSrc: x.target.result,
          });
          let linkSource = ``;
          linkSource = reader.result;
          setPdfSource(linkSource);
          setViewPDFReader(true);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        toast.error(t("documents.documentUploadErrorFileType"));
        setFile(null);
        setSubmitting(false);
      }
    } else {
      setFile();
      setSubmitting(false);
    }
  };

  const handleImageLoaded = () => {
    let img = document.getElementById("imgToPdf");
    let iWidth = img.width;
    let iHeight = img.height;

    let doc = new jsPDF("p", "px", "letter");
    let maxWidth = doc.internal.pageSize.getWidth();
    let maxHeight = doc.internal.pageSize.getHeight();
    let ratio = Math.min(maxWidth / iWidth, maxHeight / iHeight);
    iWidth = iWidth * ratio;
    iHeight = iHeight * ratio;

    doc.addImage(
      file.imageSrc,
      "JPEG",
      0,
      0,
      iWidth,
      iHeight,
      undefined,
      "FAST"
    );
    setFileName(selectedUploadName + ".pdf");
    var dataSrc = doc.output("datauristring"); // base64 string
    var blobPDF = new Blob([doc.output("blob")], {
      type: "application/pdf",
    });
    if (blobPDF.size > maxFileSize) {
      toast.error(t("documents.documentUploadErrorFileSize"));
      resetViewer();
    } else {
      setFileAsBlob(blobPDF);
      //setPdfSource(dataSrc);
      setImageData({
        imageFile: dataSrc,
        imageSrc: file.imageSrc,
      });
    }
    setSubmitting(false);
  };

  const handleDocumentLoaded = () => {
    setSubmitting(false);
  };

  const uploadClick = (uploadType) => {
    setSubmitting(true);
    let fileUpload;
    if (uploadType === "Image") {
      fileUpload = imageData.imageFile;
    } else {
      fileUpload = file.imageFile;
    }

    if (fileUpload.size >= maxFileSize) {
      toast.error(t("documents.documentUploadErrorFileSize"));
      resetViewer();
    } else {
      setValues({
        ...values,
        userId: user.userId,
        clientID: user.clientID,
        uploadTypeID: selectedUploadTypeID,
        uploadFile: fileUpload,
        uploadFileType: uploadType,
      });
    }
  };

  const sendFile = () => {
    setSubmitting(true);
    let errMsg = validate();
    if (errMsg === "") {
      let formData = new FormData();
      if (values.uploadFileType === "PDFFile") {
        formData.append("uploadFile", values.uploadFile, fileName);
      } else if (values.uploadFileType === "Image") {
        formData.append("uploadFile", fileAsBlob, fileName);
      }

      let uploadDocumentData = {
        userId: values.userId,
        clientID: values.clientID,
        uploadTypeID: values.uploadTypeID,
        uploadFile: formData,
      };
      handleUploadDocument(uploadDocumentData);
    } else {
      toast.error(t("documents.documentUploadFailed") + ": " + errMsg);
    }
    setSubmitting(false);
  };

  const validate = () => {
    let errorText = "";
    let tempValues = {};
    tempValues.uploadTypeID = values.uploadTypeID <= 0 ? false : true;
    tempValues.fileName = fileName == "" ? false : true;
    tempValues.correctFileExtension =
      isPdf(fileName) || values.uploadFile.type == "image/png" ? true : false;
    tempValues.fileSize = values.uploadFile.size >= maxFileSize ? false : true;

    if (tempValues.uploadTypeID === false) {
      errorText = t("documents.documentUploadErrorUploadType");
    } else if (tempValues.correctFileExtension === false) {
      errorText = t("documents.documentUploadErrorFileType");
    } else if (tempValues.fileSize === false) {
      errorText = t("documents.documentUploadErrorFileSize");
    } else if (Object.values(tempValues).every((x) => x == true) === false) {
      errorText =
        t("documents.documentUploadFailed") +
        ": " +
        t("documents.documentUploadDataError");
    }

    return errorText;
  };

  const resetViewer = () => {
    setPdfSource("");
    setViewPDFReader(false);
    setFile(null);
    setImageData(null);
    setSubmitting(false);
  };

  function isPdf(filename) {
    var ext = getExtension(filename);
    switch (ext.toLowerCase()) {
      case "pdf":
        return true;
    }
    return false;
  }

  function getExtension(filename) {
    var parts = filename.split(".");
    return parts[parts.length - 1];
  }

  return (
    //<div className={classes.root}>
    //</div>
    <Grid container direction="column" alignItems="center">
      {pdfSource ? (
        <div className={classes.pdfContainer}>
          <PdfViewerComponent
            id="container"
            documentPath={pdfSource}
            serviceUrl={svcUrl}
            documentLoad={handleDocumentLoaded}
          >
            <PDFInject
              services={[
                Toolbar,
                Magnification,
                Navigation,
                LinkAnnotation,
                BookmarkView,
                ThumbnailView,
                Print,
                TextSelection,
                TextSearch,
                Annotation,
                FormFields,
                FormDesigner,
              ]}
            />
          </PdfViewerComponent>
        </div>
      ) : (
        <></>
      )}
      {file && file.imageSrc === null && viewPDFReader == false ? (
        <></>
      ) : file && file.imageSrc !== null && viewPDFReader == false ? (
        <Grid item>
          <img
            id="imgToPdf"
            onLoad={() => handleImageLoaded()}
            src={file.imageSrc}
            alt={t("documents.badPhotoFile")}
            style={{ maxHeight: "100%", maxWidth: "100%" }}
          />
        </Grid>
      ) : viewPDFReader == true ? (
        <></>
      ) : (
        <></>
      )}
      {file ? (
        <Grid item>
          <Grid
            container
            direction={isXtraSmall ? "column" : "row"}
            justifyContent={isXtraSmall ? "flex-start" : "center"}
            style={{ paddingTop: 10 }}
          >
            <Grid item className={classes.tabButton}>
              <Button
                disabled={submitting}
                size="medium"
                variant="contained"
                color="primary"
                onClick={() => resetViewer()}
                startIcon={<RotateLeftIcon style={{ fontSize: "1.4em" }} />}
              >
                {t("documents.newFile")}
              </Button>
            </Grid>
            <Grid item className={classes.tabButton}>
              {file.imageSrc !== null && viewPDFReader == true ? (
                <Button
                  disabled={submitting || file === null}
                  size="medium"
                  variant="contained"
                  color="primary"
                  onClick={() => uploadClick("PDFFile")}
                  startIcon={<PublishIcon style={{ fontSize: "1.4em" }} />}
                >
                  {t("documents.upload")}
                </Button>
              ) : (
                <Button
                  disabled={submitting || file === null}
                  size="medium"
                  variant="contained"
                  color="primary"
                  onClick={() => uploadClick("Image")}
                  startIcon={<PublishIcon style={{ fontSize: "1.4em" }} />}
                >
                  {t("documents.upload")}
                </Button>
              )}
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <Grid item className={classes.labelToBtn}>
          <input
            id="actual-btn"
            type="file"
            accept="application/pdf,image/*"
            hidden
            onChange={handleFileChange}
          />
          <label htmlFor="actual-btn">{t("documents.chooseFile")}</label>
        </Grid>
      )}
    </Grid>
  );
};

UploadNow.propTypes = {
  selectedUploadTypeID: PropTypes.number,
  selectedUploadName: PropTypes.string,
  handleUploadDocument: PropTypes.func.isRequired,
};

export default UploadNow;
